import PropTypes from 'prop-types';
import { forwardRef } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';

// project import
import { ThemeMode } from 'config';

// header style
const headerSX = (theme) => ({
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.light, 0.16),
  p: 2,
  pl: 4,
  display: 'flex',
  alignItems: 'center',
  boxShadow: theme.customShadows.z1,
  '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' },
  '& .MuiCardHeader-content': { 
    display: 'flex',
    alignItems: 'center',
    height: '100%'
  },
  '&::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: alpha(theme.palette.primary.light, 0.16),
    clipPath: 'polygon(85% 0%, 100% 0, 100% 100%, 79% 100%)',

  },
  position: 'relative',
});

// ==============================|| CUSTOM - MAIN CARD ||============================== //

const MainCard = forwardRef(
  (
    {
      title,
      modal = false,
      border = true,
      boxShadow,
      children,
      subheader,
      content = true,
      contentSX = {},
      darkTitle,
      divider = true,
      elevation,
      secondary,
      shadow,
      sx = {},
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    boxShadow =
      theme.palette.mode === ThemeMode.DARK ? boxShadow || true : boxShadow;

    return (
      <Card
        elevation={elevation || 0}
        ref={ref}
        {...others}
        sx={{
          position: 'relative',
          border: border ? theme.borders.thick : 'none',
          borderRadius: 1,
          // borderColor:
          //   theme.palette.mode === ThemeMode.DARK
          //     ? theme.palette.divider
          //     : theme.palette.grey.A800,
          boxShadow:
            boxShadow && (!border || theme.palette.mode === ThemeMode.DARK)
              ? shadow || theme.customShadows.z1
              : 'inherit',
          ':hover': {
            boxShadow: boxShadow ? shadow || theme.customShadows.z1 : 'inherit',
          },
          ...(modal && {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: `calc( 100% - 50px)`, sm: 'auto' },
            '& .MuiCardContent-root': {
              minHeight: 'auto',
              maxHeight: `calc(100vh - 200px)`,
            },
          }),
          ...sx,
        }}
      >
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader
            sx={headerSX}
            titleTypographyProps={{ variant: 'h3', height: '50px', display: 'flex', alignItems: 'center' }}
            title={title}
            action={secondary}
            subheader={subheader}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={<Typography variant='h3' sx={{ display: 'flex', alignItems: 'center', height: '60px' }}>{title}</Typography>}
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {title && divider && <Divider />}

        {/* card content */}
        {content && <CardContent sx={{...contentSX}}>{children}</CardContent>}
        {!content && children}
      </Card>
    );
  }
);

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  children: PropTypes.node,
  subheader: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  content: PropTypes.bool,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.object,
  ]),
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.object,
  ]),
  modal: PropTypes.bool,
};

export default MainCard;
